import CameraIcon from 'assets/icons/camera_outline.svg?react';
import CogIcon from 'assets/icons/ic_cog.svg?react';
import MicrophoneIcon from 'assets/icons/ic_microphone.svg?react';
import LanguageIcon from 'assets/icons/ic_language.svg?react';
import PencilIcon from 'assets/icons/Pencil.svg?react';
import StudyIcon from 'assets/icons/Study.svg?react';
import DeiTestIcon from 'assets/icons/test/dei.svg?react';
import EligibilityTestIcon from 'assets/icons/test/eligibility.svg?react';
import InductiveTestIcon from 'assets/icons/test/inductive.svg?react';
import NumericalTestIcon from 'assets/icons/test/numerical.svg?react';
import PersonalityTestIcon from 'assets/icons/test/personality.svg?react';
import SkillTestIcon from 'assets/icons/test/skills_self_assessment.svg?react';
import VerbalTestIcon from 'assets/icons/test/verbal.svg?react';
import TimeIcon from 'assets/icons/Time.svg?react';
import TimerIcon from 'assets/icons/Timer.svg?react';
import { dataProtectionDocsUrl } from 'constants/deiLink';
import { StageCandidateActionType } from 'enums/stageAction';
import { TestKind } from 'enums/tests';
import {
  BEHAVIOURAL_TEST_LONG_LABEL,
  shouldUseBehaviouralLabel,
} from 'helpers/behaviouralRelabel';
import i18n from 'helpers/i18n';
import { Maybe } from 'interfaces/maybe';
import { FC } from 'react';
import { Trans } from 'react-i18next';
import { Link } from '@spotted-zebra-uk/ui-components';
import styles from './TestInfo.module.scss';

export interface ISingleTestInfoConfigNote {
  id: number;
  icon: FC;
  text: (string | JSX.Element)[];
  extraTimeConfig?: {
    text: string;
    action: 'openModal';
  };
  alt: string;
  className?: string;
  dataTestid?: string;
  accessibilityAdjustment?: number | null;
}

interface ISingleTestInfoConfig {
  icon: FC;
  title: string;
  key?: StageCandidateActionType;
  notes: ISingleTestInfoConfigNote[];
}

// Camera-proctoring disclaimer for tests whose project has integrity
// monitoring enabled. Inserted right after the description note so it reads
// above the estimated-time lines.
export const withIntegrityMonitoringNote = (
  notes: ISingleTestInfoConfigNote[]
): ISingleTestInfoConfigNote[] => [
  notes[0],
  {
    id: 99,
    icon: CameraIcon,
    text: [i18n.t('test.integrityMonitoring.notice')],
    alt: '',
    dataTestid: 'test-intro--integrity-monitoring-note',
  },
  ...notes.slice(1),
];

const getTimeTranslation = (time?: Maybe<number>, defaultTime = 15) => {
  if (time) {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;

    let timeTranslation = '';

    if (minutes > 0) {
      timeTranslation += i18n.t('common.time.minute', { count: minutes });
    }

    if (seconds > 0) {
      timeTranslation += ` ${i18n.t('common.time.second', { count: seconds })}`;
    }

    return timeTranslation;
  }

  return i18n.t('common.time.minute', {
    count: defaultTime,
  });
};

const config: {
  [key in TestKind | string]: ({
    timeRequirement,
    timePerQuestion,
    extraTimeAdded,
    companyName,
  }: {
    timeRequirement?: Maybe<number>;
    timePerQuestion?: Maybe<number>;
    extraTimeAdded?: Maybe<number>;
    companyName?: string;
    // TEMP HACK — "Personality" → "Behavioural" relabel (per-client,
    // temporary). See helpers/behaviouralRelabel.ts
    usesBehaviouralLabel?: boolean;
  }) => ISingleTestInfoConfig;
} = {
  [TestKind.CognitiveInductive]: ({
    timeRequirement,
    timePerQuestion,
    extraTimeAdded,
  }) => ({
    icon: InductiveTestIcon,
    key: StageCandidateActionType.CognitiveInductiveTest,
    title: i18n.t('test.instructions.inductive.name'),
    notes: [
      {
        id: 1,
        icon: StudyIcon,
        text: [
          i18n.t('test.instructions.inductive.description1'),
          i18n.t('test.instructions.inductive.description2'),
        ],
        alt: '',
      },
      {
        id: 2,
        icon: TimeIcon,
        text: [
          i18n.t('test.instructions.inductive.time', {
            time: getTimeTranslation(timeRequirement),
          }),
        ],
        accessibilityAdjustment: extraTimeAdded,
        alt: '',
        dataTestid: 'inductive-test--time-required',
      },
      {
        id: 3,
        icon: TimerIcon,
        text: [
          i18n.t('test.instructions.inductive.timePerQuestion', {
            time: getTimeTranslation(timePerQuestion, 1),
          }),
        ],
        extraTimeConfig: !timePerQuestion
          ? {
              text: i18n.t('common.extraTime'),
              action: 'openModal',
            }
          : undefined,
        accessibilityAdjustment: extraTimeAdded,
        className: 'TestInfoNote--Important',
        alt: '',
        dataTestid: 'inductive-test--time-required',
      },
    ],
  }),
  [TestKind.CognitiveNumerical]: ({
    timeRequirement,
    timePerQuestion,
    extraTimeAdded,
  }) => ({
    icon: NumericalTestIcon,
    key: StageCandidateActionType.CognitiveNumericalTest,
    title: i18n.t('test.instructions.numerical.name'),
    notes: [
      {
        id: 4,
        icon: StudyIcon,
        text: [
          i18n.t('test.instructions.numerical.description1'),
          i18n.t('test.instructions.numerical.description2'),
        ],
        alt: '',
      },
      {
        id: 16,
        icon: PencilIcon,
        text: [i18n.t('test.instructions.numerical.description3')],
        alt: '',
      },
      {
        id: 5,
        icon: TimeIcon,
        text: [
          i18n.t('test.instructions.numerical.time', {
            time: getTimeTranslation(timeRequirement),
          }),
        ],
        alt: '',
        accessibilityAdjustment: extraTimeAdded,
        dataTestid: 'numerical-test--time-required',
      },
      {
        id: 6,
        icon: TimerIcon,
        text: [
          i18n.t('test.instructions.numerical.timePerQuestion', {
            time: getTimeTranslation(timePerQuestion, 3),
          }),
        ],
        extraTimeConfig: !timePerQuestion
          ? { text: i18n.t('common.extraTime'), action: 'openModal' }
          : undefined,
        className: 'TestInfoNote--Important',
        accessibilityAdjustment: extraTimeAdded,
        alt: '',
        dataTestid: 'numerical-test--time-per-question',
      },
    ],
  }),
  [TestKind.Personality]: ({ timeRequirement, usesBehaviouralLabel }) => ({
    icon: PersonalityTestIcon,
    key: StageCandidateActionType.PersonalityTest,
    // TEMP HACK — "Personality" → "Behavioural" relabel (per-client,
    // temporary). See helpers/behaviouralRelabel.ts
    title: shouldUseBehaviouralLabel(usesBehaviouralLabel)
      ? BEHAVIOURAL_TEST_LONG_LABEL
      : i18n.t('test.instructions.personality.name'),
    notes: [
      {
        id: 10,
        icon: StudyIcon,
        text: [
          i18n.t('test.instructions.personality.description1'),
          i18n.t('test.instructions.personality.description2'),
        ],
        alt: '',
      },
      {
        id: 11,
        icon: TimeIcon,
        text: [
          i18n.t('test.instructions.personality.time', {
            time: getTimeTranslation(timeRequirement, 10),
          }),
        ],
        alt: '',
        dataTestid: 'personality-test--required-time',
      },
    ],
  }),
  [TestKind.CognitiveVerbal]: ({
    timeRequirement,
    timePerQuestion,
    extraTimeAdded,
  }) => ({
    icon: VerbalTestIcon,
    key: StageCandidateActionType.CognitiveVerablTest,
    title: i18n.t('test.instructions.verbal.name'),
    notes: [
      {
        id: 13,
        icon: StudyIcon,
        text: [
          i18n.t('test.instructions.verbal.description1'),
          i18n.t('test.instructions.verbal.description2'),
        ],
        alt: '',
      },
      {
        id: 14,
        icon: TimeIcon,
        text: [
          i18n.t('test.instructions.verbal.time', {
            time: getTimeTranslation(timeRequirement),
          }),
        ],
        alt: '',
        accessibilityAdjustment: extraTimeAdded,
        dataTestid: 'verbal-test--time-required',
      },
      {
        id: 15,
        icon: TimerIcon,
        text: [
          i18n.t('test.instructions.verbal.timePerQuestion', {
            time: getTimeTranslation(timePerQuestion, 3),
          }),
        ],
        extraTimeConfig: !timePerQuestion
          ? { text: i18n.t('common.extraTime'), action: 'openModal' }
          : undefined,
        className: 'TestInfoNote--Important',
        alt: '',
        accessibilityAdjustment: extraTimeAdded,
        dataTestid: 'verbal-test--time-per-question',
      },
    ],
  }),
  // The in-house English proficiency test: one fixed 60-item form on a single
  // whole-test clock, so there is no per-question allowance to quote.
  [TestKind.EnglishProficiency]: ({ timeRequirement, extraTimeAdded }) => ({
    icon: LanguageIcon,
    key: StageCandidateActionType.EnglishProficiencyTest,
    title: i18n.t('test.instructions.englishProficiency.name'),
    notes: [
      {
        id: 22,
        icon: StudyIcon,
        text: [
          i18n.t('test.instructions.englishProficiency.description1'),
          i18n.t('test.instructions.englishProficiency.description2'),
        ],
        alt: '',
      },
      {
        id: 23,
        icon: MicrophoneIcon,
        text: [i18n.t('test.instructions.englishProficiency.description3')],
        alt: '',
      },
      {
        id: 24,
        icon: TimerIcon,
        text: [
          i18n.t('test.instructions.englishProficiency.time', {
            time: getTimeTranslation(timeRequirement, 60),
          }),
        ],
        className: 'TestInfoNote--Important',
        accessibilityAdjustment: extraTimeAdded,
        alt: '',
        dataTestid: 'english-proficiency-test--time-required',
      },
    ],
  }),
  selfSkillAsessment: () => ({
    icon: SkillTestIcon,
    key: StageCandidateActionType.RssAssessment,
    title: i18n.t('assessment.skillsSelfAssessment'),
    notes: [
      {
        id: 18,
        icon: StudyIcon,
        text: [i18n.t('assessment.thisRecords')],
        alt: '',
      },
      {
        id: 19,
        icon: TimeIcon,
        text: [i18n.t('assessment.noTimeLimit')],
        alt: '',
      },
    ],
  }),
  // Eligibility questions (AI shortlist stage questionnaire) — a portal-only
  // stage, so no StageCandidateActionType key.
  eligibility: () => ({
    icon: EligibilityTestIcon,
    title: i18n.t('test.instructions.eligibility.name'),
    notes: [
      {
        id: 20,
        icon: StudyIcon,
        text: [
          i18n.t('test.instructions.eligibility.description1'),
          i18n.t('test.instructions.eligibility.description2'),
        ],
        alt: '',
      },
      {
        id: 21,
        icon: TimeIcon,
        text: [i18n.t('assessment.noTimeLimit')],
        alt: '',
        dataTestid: 'eligibility-test--required-time',
      },
    ],
  }),
  dei: ({ companyName }) => ({
    icon: DeiTestIcon,
    key: StageCandidateActionType.DeiForm,
    title: i18n.t('test.instructions.dei.name'),
    notes: [
      {
        id: 10,
        icon: StudyIcon,
        text: [
          i18n.t('test.instructions.dei.description1'),
          <Trans
            i18nKey="test.instructions.dei.description2"
            values={{ companyName }}
            components={{
              strong: <span className={styles.importantText} />,
            }}
            key="test.instructions.dei.description2"
          />,
          <Link
            key="test.instructions.dei.description4"
            to={dataProtectionDocsUrl}
            target="_blank"
            className={styles.link}
          >
            {i18n.t('test.instructions.dei.description4') as string}
          </Link>,
        ],
        alt: '',
      },
      {
        id: 11,
        icon: TimeIcon,
        text: [i18n.t('test.instructions.dei.time')],
        alt: '',
        dataTestid: 'dei-test--required-time',
      },
    ],
  }),
};

/**
 * The Coderbyte coding assessment's intro.
 *
 * It once branched on `isEnglishProficiencyTest`, because the English
 * proficiency test was a Coderbyte-hosted assessment reached through this same
 * screen. It is now an in-house test with its own intro
 * (`test.instructions.englishProficiency.*`), so this is only ever the coding
 * assessment. The `test.instructions.coding.englishProficiency*` keys are left
 * in the translation files for Crowdin to retire.
 *
 * A time *limit* is the important note -- Coderbyte enforces it -- while an
 * estimate is the quiet one. Passing neither omits the note entirely, which is
 * what an assessment with no duration on record should say.
 */
const configCodingTest = (
  timeEstimate?: Maybe<number>,
  timeRequirement?: Maybe<number>,
  extraTimeAdded?: Maybe<number>
): ISingleTestInfoConfig => {
  const timeNote = timeRequirement
    ? {
        id: 21,
        icon: TimerIcon,
        text: [
          i18n.t('test.instructions.coding.time_limit_minutes_only', {
            time: getTimeTranslation(timeRequirement),
          }),
        ],
        className: 'TestInfoNote--Important',
        accessibilityAdjustment: extraTimeAdded,
        dataTestid: 'coding-test--time-required',
      }
    : {
        id: 21,
        icon: TimeIcon,
        text: [
          i18n.t('test.instructions.coding.time_estimate_minutes_only', {
            time: getTimeTranslation(timeEstimate),
          }),
        ],
        accessibilityAdjustment: extraTimeAdded,
        dataTestid: 'coding-test--time-estimated',
      };

  return {
    icon: CogIcon,
    key: StageCandidateActionType.CodingExternalAssessment,
    title: i18n.t('test.instructions.coding.name'),
    notes: [
      {
        id: 20,
        icon: StudyIcon,
        text: [i18n.t('test.instructions.coding.description')],
        alt: '',
      },
      ...(timeEstimate || timeRequirement
        ? [
            {
              ...timeNote,
              alt: '',
            },
          ]
        : []),
    ],
  };
};

export { configCodingTest };
export default config;
